import React from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  Stack,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import { AutoAwesome as AIIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { getChatGPTResponse } from "../../../../data/GlobalData";
import { IngredientNutritionMessage } from "../../../../data/ChatGPTMessages";
import StandardButton from "../../../../components/StandardButton";
import units from "../../../../data/Units";
import {
  SHOPPING_CATEGORY_OPTIONS,
  suggestCategory,
} from "../../../../data/ShoppingCategories";
import { consola } from "consola";

const IngredientStack = ({ unit, setUnit }) => {
  const { t } = useTranslation();
  return (
    <Stack>
      <Typography>Per:</Typography>
      <Select
        sx={{ width: 100 }}
        size="small"
        value={unit}
        onChange={(event) => {
          setUnit(event.target.value);
        }}
      >
        {Object.keys(units).map((unit) => {
          return (
            <MenuItem key={unit} value={unit}>
              {t(`units.${unit}`)}
            </MenuItem>
          );
        })}
      </Select>
    </Stack>
  );
};

const IngredientEditDialog = ({
  ingredient,
  open = false,
  onDialogClosed = null,
}) => {
  const [dialogOpen, setOpen] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [name, setName] = React.useState("");
  const [proteins, setProteins] = React.useState(0);
  const [carbs, setCarbs] = React.useState(0);
  const [fats, setFats] = React.useState(0);
  const [unit, setUnit] = React.useState(null);
  const [category, setCategory] = React.useState("other");
  const [loading, setLoading] = React.useState(false);
  const [toastMessage, setToastMessage] = React.useState("");
  const [toastOpen, setToastOpen] = React.useState(false);
  const [toastSeverity, setToastSeverity] = React.useState("success");

  React.useEffect(() => {
    setOpen(open);
  }, [open]); // eslint-disable-line

  React.useEffect(() => {
    if (ingredient) {
      setName(ingredient.name);
      setProteins(ingredient?.proteins ? parseFloat(ingredient.proteins) : 0);
      setCarbs(ingredient?.carbs ? parseFloat(ingredient.carbs) : 0);
      setFats(ingredient?.fats ? parseFloat(ingredient.fats) : 0);
      setUnit(ingredient?.unit || null);
      setCategory(ingredient?.category || "other");
    } else {
      setName("");
      setProteins(0);
      setCarbs(0);
      setFats(0);
      setUnit("na");
      setCategory("other");
    }
  }, [ingredient]);

  function validate() {
    if (!name) {
      return ["A name is required."];
    }
    return null;
  }

  if (!dialogOpen) {
    return null;
  }

  return (
    <Dialog
      key="IngredientEditDialog"
      maxWidth="lg"
      open={dialogOpen}
      onClose={() => {
        if (onDialogClosed) {
          onDialogClosed(null);
        }
      }}
    >
      <DialogTitle>
        {!ingredient ? "Add Ingredient" : "Edit Ingredient"}
      </DialogTitle>

      <DialogContent>
        <Stack
          style={{
            marginTop: 24,
            marginBottom: 12,
          }}
        >
          <Typography>Ingredient:</Typography>
          <TextField
            size="small"
            value={name}
            variant="outlined"
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </Stack>

        <Stack
          style={{
            marginTop: 12,
            marginBottom: 24,
          }}
        >
          <Typography>Shopping List Category:</Typography>
          <Select
            size="small"
            value={category}
            variant="outlined"
            onChange={(event) => {
              setCategory(event.target.value);
            }}
          >
            {SHOPPING_CATEGORY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Stack>

        <Stack
          direction={"row"}
          spacing={2}
          style={{
            marginTop: 24,
            marginBottom: 24,
          }}
        >
          <IngredientStack unit={unit} setUnit={setUnit} />
          <Stack>
            <Typography>Proteins (g):</Typography>
            <TextField
              size="small"
              type="number"
              value={proteins}
              variant="outlined"
              onChange={(event) => {
                setProteins(event.target.value);
              }}
            />
          </Stack>

          <Stack>
            <Typography>Carbs (g):</Typography>
            <TextField
              size="small"
              type="number"
              value={carbs}
              variant="outlined"
              onChange={(event) => {
                setCarbs(event.target.value);
              }}
            />
          </Stack>

          <Stack>
            <Typography>Fats (g):</Typography>
            <TextField
              size="small"
              type="number"
              value={fats}
              variant="outlined"
              onChange={(event) => {
                setFats(event.target.value);
              }}
            />
          </Stack>
        </Stack>

        <Stack
          direction={"row"}
          spacing={2}
          alignItems={"flex-end"}
          style={{
            marginTop: 24,
            marginBottom: 24,
          }}
        >
          <Stack direction={"row"} spacing={1} alignItems={"center"}>
            <Button
              variant="contained"
              style={{ height: 35 }}
              startIcon={<AIIcon />}
              disabled={loading || !name || !unit}
              onClick={async () => {
                setLoading(true);
                try {
                  consola.debug(
                    "AI nutrition fill started for ingredient:",
                    name,
                  );

                  const messages = IngredientNutritionMessage({
                    ingredientName: name,
                    unit: unit,
                  });

                  const response = await getChatGPTResponse({
                    messages: messages,
                    temperature: 0.1,
                  });

                  if (response && response.response && response.response[0]) {
                    try {
                      const nutritionData = JSON.parse(
                        response.response[0].message.content,
                      );

                      if (
                        typeof nutritionData.protein === "number" &&
                        typeof nutritionData.carbs === "number" &&
                        typeof nutritionData.fats === "number"
                      ) {
                        setProteins(
                          Math.round(nutritionData.protein * 10) / 10,
                        );
                        setCarbs(Math.round(nutritionData.carbs * 10) / 10);
                        setFats(Math.round(nutritionData.fats * 10) / 10);

                        // Set category if provided by AI, otherwise use suggestion
                        if (nutritionData.category) {
                          setCategory(nutritionData.category);
                        } else {
                          setCategory(suggestCategory(name));
                        }

                        setToastMessage(
                          `AI successfully filled nutrition data and category for ${name}`,
                        );
                        setToastSeverity("success");
                        setToastOpen(true);

                        consola.success(
                          "AI nutrition fill completed:",
                          nutritionData,
                        );
                      } else {
                        throw new Error("Invalid nutrition data format");
                      }
                    } catch (parseError) {
                      consola.error("Failed to parse AI response:", parseError);
                      setToastMessage(
                        "AI returned invalid data format. Please try again.",
                      );
                      setToastSeverity("error");
                      setToastOpen(true);
                    }
                  } else {
                    throw new Error("No response from AI");
                  }
                } catch (error) {
                  consola.error("AI nutrition fill error:", error);
                  setToastMessage(
                    "Failed to get nutrition data from AI. Please try again.",
                  );
                  setToastSeverity("error");
                  setToastOpen(true);
                } finally {
                  setLoading(false);
                }
              }}
              color="primary"
            >
              Auto-fill Macros
            </Button>
            {loading && <CircularProgress size={32} variant="indeterminate" />}
            <Typography style={{ marginLeft: 8 }}>
              Use AI to automatically fill nutrition data based on the
              ingredient name and unit.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>

      {error && (
        <DialogContent>
          <Box style={{ display: "flex", alignItems: "center" }}>
            <Typography style={{ width: 200, color: "red" }}>
              {error}
            </Typography>
          </Box>
        </DialogContent>
      )}

      <DialogActions>
        <Button
          onClick={() => {
            setOpen(false);
            if (onDialogClosed) {
              onDialogClosed(null, null);
            }
          }}
          color="primary"
        >
          Cancel
        </Button>
        <StandardButton
          variant="primary"
          onClick={() => {
            const validateResult = validate();
            if (!validateResult) {
              setOpen(false);
              if (onDialogClosed) {
                onDialogClosed(
                  {
                    id: ingredient ? ingredient.id : null,
                    name,
                    proteins,
                    carbs,
                    fats,
                    unit,
                    category,
                  },
                  ingredient,
                );
              }
            } else {
              setError(validateResult[0]);
            }
          }}
        >
          Save
        </StandardButton>
      </DialogActions>

      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity={toastSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default IngredientEditDialog;
